import { useEffect, useRef, useState, useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../store'
import { setNotesFromCollection, setCurrentNote } from '../store/slices/notesSlice'
import type { Note } from '../../shared/types'
import { setContent } from '../store/slices/editorSlice'
import { getNotesCollection, onCollectionReady } from '../store/collections'
import { useAuthStore } from '../auth/store'

// 安全将任意值转换为 ISO 字符串，无法转换时返回 undefined（不抛异常）
// 解决 SQLite 读取的日期字段为 null/undefined/空字符串/非法字符串时
// new Date(value).toISOString() 抛 RangeError: Invalid Date 的问题
function safeToDateISO(value: unknown): string | undefined {
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? undefined : value.toISOString()
  }
  if (typeof value === 'string' && value.length > 0) {
    return value
  }
  return undefined
}

function serializeNote(note: any): Note {
  let tags: string[] = []
  if (Array.isArray(note.tags)) {
    tags = note.tags
  } else if (typeof note.tags === 'string') {
    try {
      const parsed = JSON.parse(note.tags)
      tags = Array.isArray(parsed) ? parsed : []
    } catch {
      tags = []
    }
  }

  const isDeleted = note.isDeleted === 'true' || note.isDeleted === true

  return {
    ...note,
    tags,
    isDeleted,
    deletedAt: safeToDateISO(note.deletedAt),
    createdAt: safeToDateISO(note.createdAt),
    updatedAt: safeToDateISO(note.updatedAt),
  }
}

let globalSubscription: { unsubscribe: () => void } | null = null
let globalRefCount = 0

export function useSyncCollectionToRedux() {
  const dispatch = useAppDispatch()
  const userId = useAuthStore((state) => state.user?.id)
  const { currentNote, notes: reduxNotes } = useAppSelector((state) => state.notes)
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [notes, setNotes] = useState<Note[]>([])
  const lastSyncRef = useRef<number>(0)
  const syncTimerRef = useRef<NodeJS.Timeout | null>(null)
  const currentNoteRef = useRef(currentNote)
  const isSubscribedRef = useRef(false)
  const isReadyRef = useRef(false)
  const lastNotesJsonRef = useRef<string>('')
  const startupFirstNotesLoggedRef = useRef(false)
  // 追踪 Redux 当前 notes，用于合并乐观插入的新笔记（防止 syncFromCollection 覆盖）
  const reduxNotesRef = useRef<Note[]>([])

  // ★ 用户切换检测：userId 变化时清空 Redux notes 与合并状态，
  // 防止换号瞬间旧用户的笔记被"乐观插入"逻辑误保留、或 UI 短暂显示上一个用户的笔记
  const lastUserIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (lastUserIdRef.current !== null && lastUserIdRef.current !== userId) {
      console.log('[useSyncCollectionToRedux] 检测到用户切换，清空 Redux notes:', lastUserIdRef.current, '→', userId)
      dispatch(setNotesFromCollection([]))
      setNotes([])
      lastNotesJsonRef.current = ''
      reduxNotesRef.current = []
      lastSyncRef.current = 0
    }
    lastUserIdRef.current = userId || null
  }, [userId, dispatch])

  useEffect(() => {
    reduxNotesRef.current = reduxNotes
  }, [reduxNotes])

  useEffect(() => {
    currentNoteRef.current = currentNote
  }, [currentNote])

  useEffect(() => {
    isReadyRef.current = isReady
  }, [isReady])

  const syncFromCollection = useCallback((changes?: any[]) => {
    try {
      const collection = getNotesCollection()
      const rawNotes = collection.toArray || []
      const isReady = collection.isReady?.() ?? true

      // 防御：collection 未就绪时（hydration/preload 未完成），不要用空数组覆盖 Redux
      // 就绪后的空是真实状态（空库），需要同步给 UI
      if (rawNotes.length === 0 && !isReady) {
        console.log('[useSyncCollectionToRedux] collection 为空且未就绪，跳过同步（等待 hydration）')
        return
      }

      const allNotes = rawNotes.map(serializeNote)

      const now = Date.now()

      // 节流：100ms 内多次调用合并为一次，但不丢弃最后一次
      if (now - lastSyncRef.current < 100 && isReadyRef.current) {
        if (syncTimerRef.current) {
          clearTimeout(syncTimerRef.current)
        }
        syncTimerRef.current = setTimeout(() => {
          syncFromCollection(changes)
        }, 100 - (now - lastSyncRef.current))
        return
      }
      
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current)
        syncTimerRef.current = null
      }
      lastSyncRef.current = now

      // 合并而非覆盖：保留乐观插入的新笔记（尚未持久化到 SQLite）
      // 场景：createNoteOptimistic 乐观插入新笔记后，syncFromCollection 的 dispatch 不应覆盖
      const currentReduxNotes = reduxNotesRef.current
      const collectionIds = new Set(allNotes.map(n => n.id))
      const optimisticNotes = currentReduxNotes.filter(n => !collectionIds.has(n.id))
      const mergedNotes = [...allNotes, ...optimisticNotes]

      setNotes(mergedNotes)

      // 内容对比：跳过无变化的 dispatch，避免不必要的 Redux 更新和组件重渲染
      const notesJson = JSON.stringify(mergedNotes)
      if (notesJson === lastNotesJsonRef.current) {
        return
      }
      lastNotesJsonRef.current = notesJson
      dispatch(setNotesFromCollection(mergedNotes))

      // [Startup] 埋点：Redux notes 首次非空
      if (allNotes.length > 0 && !startupFirstNotesLoggedRef.current) {
        startupFirstNotesLoggedRef.current = true
        console.log('[Startup] Redux notes 首次非空:', Math.round(performance.now()), 'ms', `(${allNotes.length} 条)`)
      }

      const latestCurrentNote = currentNoteRef.current
      const currentNoteId = typeof latestCurrentNote === 'string' ? latestCurrentNote : latestCurrentNote?.id

      if (currentNoteId) {
        const updatedNote = allNotes.find((n: Note) => n.id === currentNoteId)

        if (updatedNote) {
          const currentContent = typeof latestCurrentNote === 'object' ? latestCurrentNote?.content : ''
          const contentChanged = updatedNote.content !== currentContent
          const titleChanged = updatedNote.title !== (typeof latestCurrentNote === 'object' ? latestCurrentNote?.title : '')

          if (contentChanged || titleChanged) {
            // 无论本地 echo 还是远程变更，都更新 Redux 状态
            // 原因：
            // 1. Redux 应始终反映最新数据，保持单一数据源的一致性
            // 2. 本地 echo 的内容就是用户刚保存的，与编辑器内容一致，不会有冲突
            // 3. "是否应用到编辑器"由 TipTapEditor 层的焦点保护机制决定，不是靠不更新 Redux 来避免
            // 4. 如果 echo 时不更新 Redux，组件重渲染/重建时会读到旧 content，反而导致内容回退
            if (contentChanged) {
              dispatch(setContent(updatedNote.content || ''))
            }
            dispatch(setCurrentNote(updatedNote))
          }
        }
      }

      if (!isReadyRef.current) {
        setIsReady(true)
      }
    } catch (err) {
      console.error('[useSyncCollectionToRedux] 同步失败:', err)
      if (isReadyRef.current) {
        setIsReady(false)
      }
    }
  }, [dispatch])

  useEffect(() => {
    if (isSubscribedRef.current) {
      return
    }

    const setupSubscription = () => {
      try {
        const collection = getNotesCollection()
        const readyNow = typeof collection.isReady === 'function' ? collection.isReady() : true
        const count = (collection.toArray || []).length

        if (globalRefCount === 0) {
          // includeInitialState: true 确保订阅建立后立即触发一次初始快照回调
          const subscription = collection.subscribeChanges((changes: any[]) => {
            syncFromCollection(changes)
          }, { includeInitialState: true })

          globalSubscription = subscription

          // 立即同步一次当前 state（快速路径）
          syncFromCollection()

          console.log('[useSyncCollectionToRedux] 订阅建立:', {
            ready: readyNow,
            initialCount: count,
          })
        }

        globalRefCount++
        isSubscribedRef.current = true

        return () => {
          globalRefCount--
          isSubscribedRef.current = false

          if (globalRefCount === 0) {
            if (globalSubscription) {
              globalSubscription.unsubscribe()
              globalSubscription = null
            }
          }
        }
      } catch (err) {
        // Collection not initialized yet — 稍后 onCollectionReady 会重试
      }
      return undefined
    }

    let cleanup: (() => void) | undefined

    // 事件驱动：collection 未创建时注册 onCollectionReady 一次性回调（替代轮询）
    cleanup = setupSubscription()
    let unsubscribeReady: (() => void) | undefined
    if (!cleanup) {
      unsubscribeReady = onCollectionReady(() => {
        if (!cleanup) {
          cleanup = setupSubscription()
        }
      })
    }

    return () => {
      if (unsubscribeReady) {
        unsubscribeReady()
      }
      if (cleanup) {
        cleanup()
      }
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current)
      }
    }
  }, [syncFromCollection])

  return {
    isSyncing: false,
    isReady,
    error,
    lastSyncAt: lastSyncRef.current ? new Date(lastSyncRef.current) : null,
    notesCount: notes.length,
  }
}
